import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { EUR, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Schedules() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const { data: schedules = [] } = useQuery({
    queryKey: ["schedules-all", status],
    queryFn: async () => {
      const params = {};
      if (status !== "all") params.status = status;
      return (await api.get("/schedules", { params })).data;
    },
  });
  const patch = useMutation({
    mutationFn: async ({ id, ...body }) => (await api.patch(`/schedules/${id}`, body)).data,
    onSuccess: () => { toast.success("Aggiornato"); qc.invalidateQueries({ queryKey: ["schedules-all"] }); },
  });

  const counts = {
    attesa: schedules.filter(s => s.status === "attesa").length,
    in_ritardo: schedules.filter(s => s.status === "in_ritardo").length,
    pagata: schedules.filter(s => s.status === "pagata").length,
    saltata: schedules.filter(s => s.status === "saltata").length,
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader title="Scadenze" subtitle="Promesso vs ricevuto: monitora le scadenze delle donazioni."/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          ["attesa", "In attesa", "text-stone-700"],
          ["in_ritardo", "In ritardo", "text-red-700"],
          ["pagata", "Pagate", "text-emerald-700"],
          ["saltata", "Saltate", "text-stone-500"],
        ].map(([k, l, c]) => (
          <Card key={k} className="p-5 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{l}</div>
            <div className={`text-2xl font-semibold mt-1 ${c}`}>{counts[k]}</div>
          </Card>
        ))}
      </div>
      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 flex gap-3 items-center">
          <div className="text-sm font-medium">Filtri</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="attesa">In attesa</SelectItem>
              <SelectItem value="in_ritardo">In ritardo</SelectItem>
              <SelectItem value="pagata">Pagate</SelectItem>
              <SelectItem value="saltata">Saltate</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
              <TableHead className="uppercase text-[10px] tracking-widest">Scadenza</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Donatore</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest text-right">Importo</TableHead>
              <TableHead className="uppercase text-[10px] tracking-widest">Stato</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedules.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-stone-400">Nessuna scadenza.</TableCell></TableRow>}
            {schedules.map((s, i) => (
              <TableRow key={s.id} className="animate-in-row" style={{ animationDelay: `${i * 5}ms` }}>
                <TableCell className="text-sm">{fmtDate(s.due_date)}</TableCell>
                <TableCell>
                  <Link to={`/donatori/${s.donor_id}`} className="font-medium hover:underline">{s.donor_name}</Link>
                </TableCell>
                <TableCell className="text-right font-semibold">{EUR.format(s.amount)}</TableCell>
                <TableCell><StatusBadge status={s.status}/></TableCell>
                <TableCell className="text-right">
                  {s.status !== "pagata" && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs"
                      onClick={() => patch.mutate({ id: s.id, status: "saltata" })}>
                      Salta
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
